import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { useEffect, useState } from 'react';
import axios from 'axios';
import { Car, Bike, Plus, Package, Wrench, LifeBuoy, FileText, Bell, X, MapPin, Trash2, Download, Upload, AlertCircle, Wallet, Fuel, Gauge, TrendingUp, Copy, Share2, Check } from 'lucide-react';
import { toast } from '../hooks/use-toast';
import { formatBytes } from '../lib/format';
import { FUEL_TYPES } from '../mock';
import { OrderTagCodes } from '../components/OrderTagCodes';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DashboardPage() {
  const { user, token } = useAuth();
  const [tab, setTab] = useState('vehicles');
  const [vehicles, setVehicles] = useState([]);
  const [orders, setOrders] = useState([]);
  const [services, setServices] = useState([]);
  const [rsa, setRsa] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [reminders, setReminders] = useState([]);
  const [wallet, setWallet] = useState({ balance: 0, transactions: [] });
  const [walletFilter, setWalletFilter] = useState({ month: 'all', kind: 'all' });
  const [referral, setReferral] = useState(null);
  const [fuelLogs, setFuelLogs] = useState([]);
  const [fuelStats, setFuelStats] = useState({});
  const [emergencyTags, setEmergencyTags] = useState([]);
  const [openVehicle, setOpenVehicle] = useState(null);
  const [openService, setOpenService] = useState(false);
  const [openRsa, setOpenRsa] = useState(false);
  const [openUpload, setOpenUpload] = useState(false);
  const [openTopup, setOpenTopup] = useState(false);
  const [openFuel, setOpenFuel] = useState(false);
  const [copied, setCopied] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  const refresh = async () => {
    try {
      const [v, o, s, r, d, rem, w, fl, ref] = await Promise.all([
        axios.get(`${API}/vehicles`, { headers }),
        axios.get(`${API}/orders`, { headers }),
        axios.get(`${API}/service-history`, { headers }),
        axios.get(`${API}/rsa`, { headers }),
        axios.get(`${API}/documents`, { headers }),
        axios.get(`${API}/reminders`, { headers }),
        axios.get(`${API}/wallet`, { headers }),
        axios.get(`${API}/fuel-logs`, { headers }),
        axios.get(`${API}/me/referral`, { headers }),
      ]);
      setVehicles(v.data); setOrders(o.data); setServices(s.data); setRsa(r.data);
      setDocuments(d.data); setReminders(rem.data); setWallet(w.data); setFuelLogs(fl.data); setReferral(ref.data);
      const statsMap = {};
      await Promise.all(v.data.map(async (veh) => {
        try {
          const st = await axios.get(`${API}/fuel-logs/stats/${veh.id}`, { headers });
          statsMap[veh.id] = st.data;
        } catch {}
      }));
      setFuelStats(statsMap);
      try { const et = await axios.get(`${API}/qr/my-tags`, { headers }); setEmergencyTags(et.data); } catch {}
    } catch (e) { console.error(e); }
  };

  useEffect(() => { refresh(); }, []);

  return (
    <Layout>
      <section className="py-12">
        <div className="max-w-6xl mx-auto px-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <div>
              <p className="eyebrow">Owner dashboard</p>
              <h1 className="h-display text-3xl md:text-4xl mt-1">Hello, <em className="font-serif-display italic font-normal text-navy">{user?.name?.split(' ')[0]}</em></h1>
            </div>
            <div className="flex gap-2">
              <button onClick={() => setOpenRsa(true)} className="btn-outline text-sm"><LifeBuoy size={14} /> Request RSA</button>
              <button onClick={() => setOpenVehicle({})} className="btn-primary text-sm"><Plus size={14} /> Add vehicle</button>
            </div>
          </div>

          {/* Refer & Earn */}
          {referral && (
            <div className="mt-6 card-soft p-5 bg-gradient-to-br from-navy to-navy-deep text-white border-navy overflow-hidden relative">
              <div className="absolute -right-16 -top-16 w-56 h-56 rounded-full bg-white/5"></div>
              <div className="relative flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div>
                  <p className="text-xs uppercase tracking-widest text-blue-200 flex items-center gap-2">🎁 Refer & earn ₹{referral.points_per_referral} per friend</p>
                  <p className="h-display text-2xl mt-1 font-mono tracking-wide">{referral.referral_code}</p>
                  <p className="text-xs text-blue-200 mt-1">
                    {referral.successful_referrals} successful · ₹{referral.points_earned_from_referrals} earned so far
                  </p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={async () => {
                      try { await navigator.clipboard.writeText(referral.share_url); setCopied(true); toast({ title: 'Link copied' }); setTimeout(() => setCopied(false), 2000); } catch { toast({ title: 'Copy failed' }); }
                    }}
                    className="inline-flex items-center gap-2 rounded-full bg-white text-navy px-4 py-2 text-sm font-semibold hover:bg-blue-50"
                  >
                    {copied ? <Check size={14} /> : <Copy size={14} />} {copied ? 'Copied' : 'Copy link'}
                  </button>
                  <button
                    onClick={async () => {
                      if (navigator.share) {
                        try { await navigator.share({ title: 'Connect Near Me', text: `Get a Connect Near Me tag using my code ${referral.referral_code}`, url: referral.share_url }); } catch {}
                      } else {
                        try { await navigator.clipboard.writeText(referral.share_url); toast({ title: 'Link copied' }); } catch {}
                      }
                    }}
                    className="inline-flex items-center gap-2 rounded-full border border-white/25 px-4 py-2 text-sm font-semibold hover:bg-white/10"
                  >
                    <Share2 size={14} /> Share
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Reminders */}
          {reminders.length > 0 && (
            <div className="mt-6 card-soft p-5 border-l-4 border-navy">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Bell size={16} className="text-navy" />
                  <p className="font-semibold text-sm">Smart reminders</p>
                </div>
                <span className="text-xs text-neutral-500">{reminders.length} active</span>
              </div>
              <div className="grid md:grid-cols-2 gap-2">
                {reminders.slice(0, 4).map((r) => (
                  <div key={r.id} className="flex items-center gap-3 py-2 px-3 rounded-lg bg-cream">
                    <AlertCircle size={16} className={r.severity === 'overdue' ? 'text-red-500' : r.severity === 'urgent' ? 'text-orange-500' : 'text-navy'} />
                    <div className="flex-1 text-sm">
                      <p className="font-medium">{r.kind} · {r.vehicle_label}</p>
                      <p className="text-xs text-neutral-500">
                        {r.kind === 'Service'
                          ? (r.severity === 'overdue' ? `Overdue by ${Math.abs(r.days_left)} km` : `Due in ${Math.abs(r.days_left)} km`)
                          : (r.severity === 'overdue' ? `Overdue by ${Math.abs(r.days_left)} days` : `Due in ${r.days_left} days`)}
                        {' · '}{r.expires_on}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mt-8">
            <StatCard icon={Car} label="Vehicles" value={vehicles.length} />
            <StatCard icon={Package} label="Orders" value={orders.length} />
            <StatCard icon={Wallet} label="Wallet" value={`₹${wallet.balance || 0}`} />
            <StatCard icon={Wrench} label="Services" value={services.length} />
            <StatCard icon={LifeBuoy} label="RSA" value={rsa.length} />
            <StatCard icon={FileText} label="Docs" value={documents.length} />
          </div>

          {/* Tabs */}
          <div className="mt-8 border-b flex gap-6 overflow-x-auto">
            {[
              ['vehicles', 'Vehicles'],
              ['emergency_tags', 'Emergency Tags'],
              ['fuel', 'Fuel Tracker'],
              ['documents', 'Documents'],
              ['services', 'Service History'],
              ['rsa', 'RSA Requests'],
              ['orders', 'Orders'],
              ['wallet', 'Wallet'],
            ].map(([k, l]) => (
              <button key={k} onClick={() => setTab(k)} className={`py-3 text-sm font-medium border-b-2 whitespace-nowrap ${tab === k ? 'border-navy text-navy' : 'border-transparent text-neutral-500 hover:text-navy'}`}>{l}</button>
            ))}
          </div>

          {/* Panels */}
          <div className="mt-6">
            {tab === 'emergency_tags' && (
              <div className="space-y-4" data-testid="my-emergency-tags">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div><h3 className="h-display text-2xl">My Emergency Tags</h3><p className="text-xs text-neutral-500">All tags allocated to your account, with lifecycle status.</p></div>
                  <a href="/activate" className="btn-primary text-sm" data-testid="activate-tag-cta">Activate new tag</a>
                </div>
                {emergencyTags.length === 0 && <EmptyState icon={Car} title="No emergency tags yet" desc="Once your order ships and admin allocates a tag, activate it from here or scan the sticker." />}
                <div className="grid md:grid-cols-2 gap-4">
                  {emergencyTags.map((t) => (
                    <div key={t.tag_id} className="card-soft p-5" data-testid={`etag-${t.tag_id}`}>
                      <div className="flex items-start justify-between">
                        <div>
                          <p className="font-mono text-sm font-semibold">{t.tag_id}</p>
                          <p className="text-xs text-neutral-500 mt-0.5">{t.permanent_url}</p>
                        </div>
                        <span className={`text-[10px] px-2 py-1 rounded-full font-semibold uppercase ${t.status === 'active' ? 'bg-green-100 text-green-700' : t.status === 'allocated' ? 'bg-yellow-100 text-yellow-700' : 'bg-neutral-100 text-neutral-600'}`}>{t.status}</span>
                      </div>
                      {t.vehicle && <div className="mt-3 text-sm"><p className="font-semibold">{t.vehicle.make} {t.vehicle.model} · <span className="font-mono">{t.vehicle.registration_number}</span></p><p className="text-xs text-neutral-500">{t.vehicle.colour} · {t.vehicle.vehicle_type}</p></div>}
                      <div className="mt-3 grid grid-cols-3 gap-2 text-xs">
                        <div className="bg-cream rounded-lg px-2 py-1.5 text-center"><p className="text-neutral-500">Scans</p><p className="font-semibold">{t.scan_count}</p></div>
                        <div className="bg-cream rounded-lg px-2 py-1.5 text-center"><p className="text-neutral-500">Notifs</p><p className="font-semibold">{t.notification_count}</p></div>
                        <div className="bg-cream rounded-lg px-2 py-1.5 text-center"><p className="text-neutral-500">Last scan</p><p className="font-semibold">{t.last_scan_at ? new Date(t.last_scan_at).toLocaleDateString() : '—'}</p></div>
                      </div>
                      {t.status !== 'active' && <a href={`/activate?tag=${t.tag_id}`} className="mt-3 inline-flex text-xs text-navy font-semibold hover:underline">Activate now →</a>}
                      {t.status === 'active' && <a href={`/tag/${t.tag_id.replace('TAG', '')}`} target="_blank" rel="noreferrer" className="mt-3 inline-flex text-xs text-navy font-semibold hover:underline">View public profile →</a>}
                      {t.activated_at && <p className="mt-2 text-[10px] text-neutral-400">Activated {new Date(t.activated_at).toLocaleString()}</p>}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {tab === 'vehicles' && (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {vehicles.length === 0 && <EmptyState icon={Car} title="No vehicles yet" desc="Add your first bike or car to activate a Connect tag." action={<button onClick={() => setOpenVehicle({})} className="btn-primary text-sm mt-4"><Plus size={14} /> Add vehicle</button>} />}
                {vehicles.map((v) => (
                  <div key={v.id} className="card-soft p-5">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-11 h-11 rounded-xl bg-navy text-white flex items-center justify-center">{v.type === 'bike' ? <Bike size={18} /> : <Car size={18} />}</div>
                        <div>
                          <p className="font-semibold">{v.make} {v.model}</p>
                          <p className="text-xs text-neutral-500">{v.number_plate}</p>
                        </div>
                      </div>
                      <span className="text-[10px] px-2 py-1 rounded-full bg-navy/5 text-navy font-semibold">Tag #{v.tag_id?.slice(-6)}</span>
                    </div>
                    <div className="grid grid-cols-2 gap-2 mt-4 text-xs">
                      <Info label="Blood group" value={v.blood_group || '—'} />
                      <Info label="Emergency" value={v.emergency_contact || '—'} />
                      <Info label="Insurance" value={v.insurance_expiry || '—'} />
                      <Info label="PUC" value={v.puc_expiry || '—'} />
                    </div>
                    <div className="flex gap-2 mt-4">
                      <button onClick={() => setOpenVehicle(v)} className="btn-ghost text-xs !py-1.5 !px-3">Edit</button>
                      <a href={`/scan/${v.tag_id}`} target="_blank" rel="noreferrer" className="btn-ghost text-xs !py-1.5 !px-3">Test scan</a>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {tab === 'services' && (
              <div>
                <div className="flex justify-end mb-3"><button onClick={() => setOpenService(true)} className="btn-primary text-sm"><Plus size={14} /> Add service log</button></div>
                {services.length === 0 && <EmptyState icon={Wrench} title="No service logs" desc="Add service records to track maintenance history." />}
                <div className="space-y-3">
                  {services.map((s) => (
                    <div key={s.id} className="card-soft p-5 flex justify-between items-center">
                      <div>
                        <p className="font-semibold">{s.service_type}</p>
                        <p className="text-xs text-neutral-500 mt-1">{s.workshop} · {s.date}</p>
                        <p className="text-xs text-neutral-600 mt-2">{s.notes}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-navy">&#8377;{s.cost}</p>
                        <p className="text-xs text-neutral-500">{s.odometer} km</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {tab === 'wallet' && (
              <div>
                <div className="card-soft p-6 bg-navy text-white border-navy flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                  <div>
                    <p className="text-xs uppercase tracking-widest text-blue-200">Wallet balance</p>
                    <p className="h-display text-4xl mt-2">₹{wallet.balance || 0}</p>
                    <p className="text-xs text-blue-200 mt-1">1 point = ₹1 · Validity {wallet.validity_days || 365} days from credit</p>
                    {wallet.expiring_soon > 0 && (
                      <p className="text-xs text-yellow-200 mt-2">⚠ ₹{wallet.expiring_soon} expiring in the next 30 days — use them soon!</p>
                    )}
                  </div>
                  <button onClick={() => setOpenTopup(true)} className="inline-flex items-center gap-2 rounded-full bg-white text-navy px-5 py-2.5 font-semibold text-sm hover:bg-blue-50">
                    <Plus size={14} /> Top up wallet
                  </button>
                </div>

                <h3 className="font-semibold mt-8 mb-3 text-sm flex items-center justify-between">
                  Transactions
                  {wallet.transactions.length > 0 && (
                    <button
                      onClick={async () => {
                        try {
                          const r = await axios.get(`${API}/wallet/export.csv`, { headers, responseType: 'blob' });
                          const url = URL.createObjectURL(r.data);
                          const a = document.createElement('a');
                          a.href = url; a.download = `wallet-${new Date().toISOString().slice(0,10)}.csv`; a.click();
                          setTimeout(() => URL.revokeObjectURL(url), 3000);
                          toast({ title: 'CSV downloaded' });
                        } catch { toast({ title: 'Download failed' }); }
                      }}
                      className="btn-outline text-xs !py-1.5 !px-3 font-normal"
                    >
                      <Download size={12} /> Download CSV
                    </button>
                  )}
                </h3>

                {wallet.transactions.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-3">
                    <select value={walletFilter.month} onChange={(e) => setWalletFilter({ ...walletFilter, month: e.target.value })} className="px-3 py-2 rounded-lg border border-black/15 bg-white text-sm">
                      <option value="all">All months</option>
                      <option value="0">This month</option>
                      <option value="1">Last month</option>
                      <option value="3">Last 3 months</option>
                      <option value="6">Last 6 months</option>
                      <option value="12">Last 12 months</option>
                    </select>
                    <select value={walletFilter.kind} onChange={(e) => setWalletFilter({ ...walletFilter, kind: e.target.value })} className="px-3 py-2 rounded-lg border border-black/15 bg-white text-sm">
                      <option value="all">All types</option>
                      <option value="credits">Credits only</option>
                      <option value="debits">Debits only</option>
                      <option value="topup">Topups</option>
                      <option value="referral_reward">Referral bonuses</option>
                      <option value="referral_cashback">Cashback</option>
                      <option value="redeem">Used at checkout</option>
                      <option value="expiry">Expired</option>
                      <option value="admin_credit">Admin credits</option>
                    </select>
                  </div>
                )}
                {wallet.transactions.length === 0 && <EmptyState icon={Wallet} title="No transactions yet" desc="Top up your wallet or refer a friend to earn points." />}
                <div className="space-y-2">
                  {(() => {
                    const now = new Date();
                    let filtered = wallet.transactions.filter((t) => {
                      // Month filter
                      if (walletFilter.month !== 'all') {
                        const m = parseInt(walletFilter.month, 10);
                        const d = new Date(t.created_at);
                        if (m === 0) {
                          if (d.getMonth() !== now.getMonth() || d.getFullYear() !== now.getFullYear()) return false;
                        } else {
                          const cutoff = new Date(now.getFullYear(), now.getMonth() - m, now.getDate());
                          if (d < cutoff) return false;
                        }
                      }
                      // Kind filter
                      if (walletFilter.kind === 'credits' && (t.amount || 0) < 0) return false;
                      if (walletFilter.kind === 'debits' && (t.amount || 0) >= 0) return false;
                      if (!['all', 'credits', 'debits'].includes(walletFilter.kind) && t.kind !== walletFilter.kind) return false;
                      return true;
                    });
                    if (filtered.length === 0) return <p className="text-sm text-neutral-500 text-center py-6">No transactions match this filter.</p>;
                    return filtered.map((t) => {
                    const isCredit = (t.amount || 0) >= 0;
                    const labels = { topup: 'Wallet topup', referral_reward: 'Referral bonus', referral_cashback: 'Cashback', admin_credit: 'Admin credit', redeem: 'Used at checkout', expiry: 'Points expired' };
                    return (
                      <div key={t.id} className="card-soft p-4 flex justify-between items-center">
                        <div>
                          <p className="text-sm font-medium">{labels[t.kind] || t.kind}</p>
                          <p className="text-xs text-neutral-500">{new Date(t.created_at).toLocaleString()}{t.order_number ? ` · #${t.order_number}` : ''}</p>
                          {t.note && <p className="text-[11px] text-neutral-500 mt-0.5">{t.note}</p>}
                        </div>
                        <div className="text-right">
                          <p className={`font-bold ${isCredit ? 'text-green-600' : 'text-neutral-600'}`}>{isCredit ? '+' : ''}₹{t.amount}</p>
                          {t.expires_at && !t.expired && isCredit && <span className="text-[10px] text-neutral-500">exp {t.expires_at.slice(0, 10)}</span>}
                          {t.expired && <span className="text-[10px] text-red-500">expired</span>}
                        </div>
                      </div>
                    );
                  });})()}
                </div>
              </div>
            )}

            {tab === 'fuel' && (
              <div>
                <div className="flex justify-end mb-3">
                  <button onClick={() => setOpenFuel(true)} disabled={vehicles.length === 0} className="btn-primary text-sm disabled:opacity-60"><Plus size={14} /> Add fill-up</button>
                </div>

                {vehicles.length === 0 && <EmptyState icon={Fuel} title="Add a vehicle first" desc="Track fuel and mileage for each vehicle in your garage." />}

                {vehicles.map((v) => {
                  const st = fuelStats[v.id] || {};
                  return (
                    <div key={v.id} className="card-soft p-5 mb-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-xl bg-navy text-white flex items-center justify-center">{v.type === 'bike' ? <Bike size={16} /> : <Car size={16} />}</div>
                          <div>
                            <p className="font-semibold">{v.make} {v.model}</p>
                            <p className="text-xs text-neutral-500">{v.number_plate} · {st.fuel_type ? st.fuel_type.toUpperCase() : 'No logs yet'}</p>
                          </div>
                        </div>
                        <span className="text-xs text-neutral-500">{st.entries || 0} entries</span>
                      </div>

                      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-4">
                        <MiniStat icon={Gauge} label="Avg mileage" value={st.avg_mileage ? `${st.avg_mileage} km/L` : '—'} />
                        <MiniStat icon={TrendingUp} label="Last mileage" value={st.latest_mileage ? `${st.latest_mileage} km/L` : '—'} />
                        <MiniStat icon={Fuel} label="Total litres" value={st.total_litres != null ? `${st.total_litres} L` : '—'} />
                        <MiniStat icon={Wallet} label="Cost / km" value={st.cost_per_km ? `₹${st.cost_per_km}` : '—'} />
                      </div>

                      {st.trend && st.trend.length > 0 && (
                        <div className="mt-4">
                          <p className="text-xs text-neutral-500 mb-2">Recent fills</p>
                          <div className="flex gap-1.5 items-end h-16">
                            {st.trend.map((tr, i) => {
                              const maxM = Math.max(...st.trend.map((t) => t.mileage));
                              const h = maxM > 0 ? (tr.mileage / maxM) * 100 : 0;
                              return (
                                <div key={i} className="flex-1 flex flex-col items-center">
                                  <div className="w-full bg-navy rounded-t" style={{ height: `${Math.max(h, 6)}%` }} />
                                  <p className="text-[9px] text-neutral-500 mt-1">{tr.mileage}</p>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}

                {fuelLogs.length > 0 && (
                  <div className="mt-6">
                    <h3 className="font-semibold text-sm mb-3">All fill-ups</h3>
                    <div className="space-y-2">
                      {fuelLogs.slice().reverse().map((l) => {
                        const v = vehicles.find((x) => x.id === l.vehicle_id);
                        return (
                          <div key={l.id} className="card-soft p-4 flex justify-between items-center">
                            <div>
                              <p className="text-sm font-medium">{v ? `${v.make} ${v.model}` : 'Vehicle'} · {l.fuel_type.toUpperCase()}</p>
                              <p className="text-xs text-neutral-500">{l.date} · Odometer {l.odometer} km · {l.litres} L @ ₹{l.price_per_litre}/L</p>
                            </div>
                            <div className="flex items-center gap-3">
                              <p className="font-bold text-navy">₹{l.amount}</p>
                              <button onClick={async () => {
                                if (!window.confirm('Delete this fill-up?')) return;
                                try { await axios.delete(`${API}/fuel-logs/${l.id}`, { headers }); toast({ title: 'Deleted' }); refresh(); } catch { toast({ title: 'Failed' }); }
                              }} className="p-1 text-neutral-500 hover:text-red-500"><Trash2 size={14} /></button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}

            {tab === 'documents' && (
              <div>
                <div className="flex justify-end mb-3">
                  <button onClick={() => setOpenUpload(true)} disabled={vehicles.length === 0} className="btn-primary text-sm disabled:opacity-60">
                    <Upload size={14} /> Upload document
                  </button>
                </div>
                {documents.length === 0 && (
                  <EmptyState
                    icon={FileText}
                    title="No documents yet"
                    desc={vehicles.length === 0 ? 'Add a vehicle first, then upload RC, Insurance and PUC.' : 'Upload your first document to the Digital Wallet.'}
                  />
                )}
                <div className="grid md:grid-cols-2 gap-3">
                  {documents.map((d) => {
                    const v = vehicles.find((x) => x.id === d.vehicle_id);
                    return (
                      <div key={d.id} className="card-soft p-4 flex gap-3 items-center">
                        <div className="w-11 h-11 rounded-xl bg-navy text-white flex items-center justify-center"><FileText size={18} /></div>
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-sm truncate">{d.file_name}</p>
                          <p className="text-xs text-neutral-500">
                            <span className="uppercase tracking-wider text-navy font-semibold">{d.doc_type}</span>
                            {v ? ` · ${v.make} ${v.model}` : ''} · {formatBytes(d.size_bytes)}
                          </p>
                        </div>
                        <button
                          onClick={async () => {
                            try {
                              const { data } = await axios.get(`${API}/documents/${d.id}/download`, { headers });
                              const blob = new Blob([Uint8Array.from(atob(data.data_base64), (c) => c.charCodeAt(0))], { type: data.mime_type });
                              const url = URL.createObjectURL(blob);
                              const a = document.createElement('a');
                              a.href = url; a.download = data.file_name; a.click();
                              setTimeout(() => URL.revokeObjectURL(url), 5000);
                            } catch { toast({ title: 'Download failed' }); }
                          }}
                          className="p-2 text-neutral-500 hover:text-navy"
                        >
                          <Download size={15} />
                        </button>
                        <button
                          onClick={async () => {
                            if (!window.confirm('Delete this document?')) return;
                            try {
                              await axios.delete(`${API}/documents/${d.id}`, { headers });
                              toast({ title: 'Document deleted' });
                              refresh();
                            } catch { toast({ title: 'Failed' }); }
                          }}
                          className="p-2 text-neutral-500 hover:text-red-500"
                        >
                          <Trash2 size={15} />
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {tab === 'rsa' && (
              <div>
                {rsa.length === 0 && <EmptyState icon={LifeBuoy} title="No RSA requests" desc="Anytime you need roadside help, tap Request RSA." />}
                <div className="space-y-3">
                  {rsa.map((r) => (
                    <div key={r.id} className="card-soft p-5">
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="font-semibold">{r.issue_type}</p>
                          <p className="text-xs text-neutral-500 mt-1"><MapPin size={11} className="inline mr-1" />{r.location}</p>
                          <p className="text-xs text-neutral-600 mt-2">{r.notes}</p>
                        </div>
                        <span className={`text-[10px] px-2 py-1 rounded-full font-semibold ${r.status === 'resolved' ? 'bg-green-100 text-green-700' : r.status === 'in_progress' ? 'bg-yellow-100 text-yellow-700' : 'bg-blue-100 text-blue-700'}`}>{r.status}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {tab === 'orders' && (
              <div className="space-y-3">
                {orders.length === 0 && <EmptyState icon={Package} title="No orders yet" desc="Buy a Connect tag to see your orders here." />}
                {orders.map((o) => (
                  <div key={o.id} className="card-soft p-5">
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-semibold">Order #{o.order_number}</p>
                        <p className="text-xs text-neutral-500 mt-1">{new Date(o.created_at).toLocaleDateString()}</p>
                        <p className="text-xs text-neutral-600 mt-2">{o.items.map((i) => `${i.name} x${i.qty}`).join(', ')}</p>
                        {o.wallet_used > 0 && <p className="text-[11px] text-navy mt-1">&#8377;{o.wallet_used} paid via wallet</p>}
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-navy">&#8377;{o.total}</p>
                        <span className={`text-[10px] px-2 py-1 rounded-full font-semibold uppercase ${o.status === 'paid' ? 'bg-green-100 text-green-700' : 'bg-navy/10 text-navy'}`}>{o.status}</span>
                      </div>
                    </div>
                    {(o.status === 'paid' || o.payment_mode === 'cod') && o.qr_tags?.length > 0 && <OrderTagCodes tags={o.qr_tags} delivered={o.shipment_status === 'delivered'} />}
                    {(o.status === 'paid' || o.payment_mode === 'cod') && (
                      <div className="mt-3 pt-3 border-t">
                        <OrderTimeline status={o.shipment_status || 'placed'} tracking_id={o.tracking_id} carrier={o.carrier} />
                        <div className="mt-3 flex gap-2 flex-wrap">
                          <button
                            onClick={async () => {
                              try {
                                const r = await axios.get(`${API}/orders/${o.id}/invoice.pdf`, { headers, responseType: 'blob' });
                                const url = URL.createObjectURL(r.data);
                                const a = document.createElement('a');
                                a.href = url; a.download = `invoice-${o.order_number}.pdf`; a.click();
                                setTimeout(() => URL.revokeObjectURL(url), 3000);
                                toast({ title: 'Invoice downloaded' });
                              } catch { toast({ title: 'Download failed' }); }
                            }}
                            className="btn-outline text-xs !py-1.5 !px-3"
                          >
                            <Download size={12} /> GST Invoice
                          </button>
                          <a href={`/track/${o.order_number}`} target="_blank" rel="noreferrer" className="btn-ghost text-xs !py-1.5 !px-3">
                            <MapPin size={12} /> Track / Share
                          </a>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      {openVehicle && <VehicleModal initial={openVehicle} onClose={() => setOpenVehicle(null)} onSaved={refresh} headers={headers} />}
      {openService && <ServiceModal vehicles={vehicles} onClose={() => setOpenService(false)} onSaved={refresh} headers={headers} />}
      {openRsa && <RsaModal vehicles={vehicles} onClose={() => setOpenRsa(false)} onSaved={refresh} headers={headers} />}
      {openUpload && <DocumentUploadModal vehicles={vehicles} onClose={() => setOpenUpload(false)} onSaved={refresh} headers={headers} />}
      {openTopup && <TopupModal onClose={() => setOpenTopup(false)} onSaved={refresh} headers={headers} />}
      {openFuel && <FuelModal vehicles={vehicles} onClose={() => setOpenFuel(false)} onSaved={refresh} headers={headers} />}
    </Layout>
  );
}

function StatCard({ icon: Icon, label, value }) {
  return (
    <div className="card-soft p-4">
      <div className="flex items-center gap-2 text-neutral-500 text-xs"><Icon size={14} /> {label}</div>
      <p className="h-display text-3xl mt-2">{value}</p>
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div className="bg-cream rounded-lg p-2">
      <p className="text-[10px] text-neutral-500 uppercase tracking-wider">{label}</p>
      <p className="text-xs font-medium mt-0.5 truncate">{value}</p>
    </div>
  );
}

function EmptyState({ icon: Icon, title, desc, action }) {
  return (
    <div className="card-soft p-10 text-center">
      <Icon size={32} className="mx-auto text-neutral-400" />
      <p className="mt-3 font-semibold">{title}</p>
      <p className="text-sm text-neutral-500">{desc}</p>
      {action}
    </div>
  );
}

function Modal({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-end sm:items-center justify-center p-4" onClick={onClose}>
      <div className="card-soft w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-center mb-5"><h3 className="font-semibold text-lg">{title}</h3><button onClick={onClose} className="p-1 rounded-full hover:bg-black/5"><X size={18} /></button></div>
        {children}
      </div>
    </div>
  );
}

function VehicleModal({ initial, onClose, onSaved, headers }) {
  const [f, setF] = useState({ type: 'car', make: '', model: '', number_plate: '', blood_group: '', emergency_contact: '', insurance_expiry: '', puc_expiry: '', ...initial });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      if (initial?.id) await axios.put(`${API}/vehicles/${initial.id}`, f, { headers });
      else await axios.post(`${API}/vehicles`, f, { headers });
      toast({ title: initial?.id ? 'Vehicle updated' : 'Vehicle added — tag activated!' });
      onSaved(); onClose();
    } catch (e) { toast({ title: 'Failed', description: e.response?.data?.detail || 'Try again' }); }
    finally { setBusy(false); }
  };
  return (
    <Modal title={initial?.id ? 'Edit vehicle' : 'Add vehicle'} onClose={onClose}>
      <div className="grid grid-cols-2 gap-3">
        <div className="col-span-2"><label className="text-xs">Type</label>
          <div className="grid grid-cols-2 gap-2 mt-1">
            {['car', 'bike'].map((t) => (
              <button key={t} onClick={() => setF({ ...f, type: t })} className={`py-2 rounded-lg text-sm font-semibold capitalize border-2 ${f.type === t ? 'bg-navy text-white border-navy' : 'border-black/10'}`}>{t}</button>
            ))}
          </div>
        </div>
        <Field label="Make" value={f.make} onChange={(v) => setF({ ...f, make: v })} />
        <Field label="Model" value={f.model} onChange={(v) => setF({ ...f, model: v })} />
        <Field label="Number plate" value={f.number_plate} onChange={(v) => setF({ ...f, number_plate: v })} />
        <Field label="Blood group" value={f.blood_group} onChange={(v) => setF({ ...f, blood_group: v })} placeholder="O+, B+, ..." />
        <Field label="Emergency contact" value={f.emergency_contact} onChange={(v) => setF({ ...f, emergency_contact: v })} placeholder="+91..." />
        <Field label="Insurance expiry" value={f.insurance_expiry} onChange={(v) => setF({ ...f, insurance_expiry: v })} placeholder="YYYY-MM-DD" />
        <Field label="PUC expiry" value={f.puc_expiry} onChange={(v) => setF({ ...f, puc_expiry: v })} placeholder="YYYY-MM-DD" />
      </div>
      <button disabled={busy} onClick={save} className="btn-primary w-full justify-center mt-5 disabled:opacity-60">{busy ? 'Saving...' : 'Save vehicle'}</button>
    </Modal>
  );
}

function ServiceModal({ vehicles, onClose, onSaved, headers }) {
  const [f, setF] = useState({ vehicle_id: vehicles[0]?.id || '', service_type: '', workshop: '', date: '', cost: '', odometer: '', notes: '' });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/service-history`, { ...f, cost: Number(f.cost) || 0, odometer: Number(f.odometer) || 0 }, { headers });
      toast({ title: 'Service log added' });
      onSaved(); onClose();
    } catch (e) { toast({ title: 'Failed', description: e.response?.data?.detail }); }
    finally { setBusy(false); }
  };
  return (
    <Modal title="Add service log" onClose={onClose}>
      <div className="grid grid-cols-2 gap-3">
        <div className="col-span-2"><label className="text-xs">Vehicle</label>
          <select value={f.vehicle_id} onChange={(e) => setF({ ...f, vehicle_id: e.target.value })} className="mt-1 w-full px-3 py-2.5 rounded-lg border border-black/15">
            {vehicles.map((v) => <option key={v.id} value={v.id}>{v.make} {v.model} ({v.number_plate})</option>)}
          </select>
        </div>
        <Field label="Service type" value={f.service_type} onChange={(v) => setF({ ...f, service_type: v })} placeholder="Oil change / Tyre / ..." />
        <Field label="Workshop" value={f.workshop} onChange={(v) => setF({ ...f, workshop: v })} />
        <Field label="Date" value={f.date} onChange={(v) => setF({ ...f, date: v })} placeholder="YYYY-MM-DD" />
        <Field label="Odometer (km)" value={f.odometer} onChange={(v) => setF({ ...f, odometer: v })} />
        <Field label="Cost (INR)" value={f.cost} onChange={(v) => setF({ ...f, cost: v })} />
        <div className="col-span-2"><label className="text-xs">Notes</label><textarea rows={2} value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} className="mt-1 w-full px-3 py-2.5 rounded-lg border border-black/15 resize-none" /></div>
      </div>
      <button disabled={busy} onClick={save} className="btn-primary w-full justify-center mt-5 disabled:opacity-60">{busy ? 'Saving...' : 'Add log'}</button>
    </Modal>
  );
}

function RsaModal({ vehicles, onClose, onSaved, headers }) {
  const [f, setF] = useState({ vehicle_id: vehicles[0]?.id || '', issue_type: 'Flat tyre', location: '', notes: '' });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/rsa`, f, { headers });
      toast({ title: 'RSA requested', description: 'Our partner will call you in 5 minutes.' });
      onSaved(); onClose();
    } catch (e) { toast({ title: 'Failed', description: e.response?.data?.detail }); }
    finally { setBusy(false); }
  };
  return (
    <Modal title="Request Roadside Assistance" onClose={onClose}>
      <div className="space-y-3">
        <div><label className="text-xs">Vehicle</label>
          <select value={f.vehicle_id} onChange={(e) => setF({ ...f, vehicle_id: e.target.value })} className="mt-1 w-full px-3 py-2.5 rounded-lg border border-black/15">
            {vehicles.length === 0 && <option>No vehicles — add one first</option>}
            {vehicles.map((v) => <option key={v.id} value={v.id}>{v.make} {v.model} ({v.number_plate})</option>)}
          </select>
        </div>
        <div><label className="text-xs">Issue</label>
          <select value={f.issue_type} onChange={(e) => setF({ ...f, issue_type: e.target.value })} className="mt-1 w-full px-3 py-2.5 rounded-lg border border-black/15">
            {['Flat tyre', 'Battery jump-start', 'Fuel delivery', 'Towing', 'Minor repair', 'Other'].map((o) => <option key={o}>{o}</option>)}
          </select>
        </div>
        <Field label="Location" value={f.location} onChange={(v) => setF({ ...f, location: v })} placeholder="NH-48, near Hebbal" />
        <div><label className="text-xs">Notes</label><textarea rows={2} value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} className="mt-1 w-full px-3 py-2.5 rounded-lg border border-black/15 resize-none" /></div>
      </div>
      <button disabled={busy || vehicles.length === 0} onClick={save} className="btn-primary w-full justify-center mt-5 disabled:opacity-60">{busy ? 'Requesting...' : 'Request help'}</button>
    </Modal>
  );
}

function Field({ label, value, onChange, placeholder }) {
  return (
    <div>
      <label className="text-xs">{label}</label>
      <input value={value || ''} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="mt-1 w-full px-3 py-2.5 rounded-lg border border-black/15 focus:ring-2 focus:ring-navy/30 focus:outline-none" />
    </div>
  );
}

function DocumentUploadModal({ vehicles, onClose, onSaved, headers }) {
  const [f, setF] = useState({ vehicle_id: vehicles[0]?.id || '', doc_type: 'rc', note: '' });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  const readAsBase64 = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result.split(',')[1]);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

  const save = async () => {
    if (!file) { toast({ title: 'Pick a file first' }); return; }
    if (file.size > 4 * 1024 * 1024) { toast({ title: 'Max 4 MB' }); return; }
    setBusy(true);
    try {
      const b64 = await readAsBase64(file);
      await axios.post(`${API}/documents`, {
        vehicle_id: f.vehicle_id,
        doc_type: f.doc_type,
        file_name: file.name,
        mime_type: file.type || 'application/octet-stream',
        data_base64: b64,
        note: f.note,
      }, { headers });
      toast({ title: 'Document uploaded' });
      onSaved(); onClose();
    } catch (e) {
      toast({ title: 'Upload failed', description: e.response?.data?.detail || 'Try again' });
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-end sm:items-center justify-center p-4" onClick={onClose}>
      <div className="card-soft w-full max-w-lg p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-center mb-5">
          <h3 className="font-semibold text-lg">Upload document</h3>
          <button onClick={onClose} className="p-1 rounded-full hover:bg-black/5"><X size={18} /></button>
        </div>
        <div className="space-y-3">
          <div><label className="text-xs">Vehicle</label>
            <select value={f.vehicle_id} onChange={(e) => setF({ ...f, vehicle_id: e.target.value })} className="mt-1 w-full px-3 py-2.5 rounded-lg border border-black/15">
              {vehicles.map((v) => <option key={v.id} value={v.id}>{v.make} {v.model} ({v.number_plate})</option>)}
            </select>
          </div>
          <div><label className="text-xs">Document type</label>
            <select value={f.doc_type} onChange={(e) => setF({ ...f, doc_type: e.target.value })} className="mt-1 w-full px-3 py-2.5 rounded-lg border border-black/15">
              <option value="rc">Registration Certificate (RC)</option>
              <option value="insurance">Insurance</option>
              <option value="puc">PUC</option>
              <option value="license">Driving License</option>
              <option value="other">Other</option>
            </select>
          </div>
          <div><label className="text-xs">File (PDF or image, max 4 MB)</label>
            <input type="file" accept="application/pdf,image/*" onChange={(e) => setFile(e.target.files?.[0] || null)} className="mt-1 w-full text-sm" />
            {file && <p className="text-xs text-neutral-500 mt-1">{file.name} · {formatBytes(file.size)}</p>}
          </div>
          <div><label className="text-xs">Note (optional)</label>
            <textarea rows={2} value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} className="mt-1 w-full px-3 py-2.5 rounded-lg border border-black/15 resize-none" />
          </div>
        </div>
        <button disabled={busy} onClick={save} className="btn-primary w-full justify-center mt-5 disabled:opacity-60">{busy ? 'Uploading…' : 'Upload'}</button>
      </div>
    </div>
  );
}

function MiniStat({ icon: Icon, label, value }) {
  return (
    <div className="bg-cream rounded-lg p-2.5">
      <div className="flex items-center gap-1.5 text-neutral-500 text-[10px] uppercase tracking-wider"><Icon size={11} /> {label}</div>
      <p className="font-semibold text-sm mt-0.5">{value}</p>
    </div>
  );
}

function TopupModal({ onClose, onSaved, headers }) {
  const [amount, setAmount] = useState(100);
  const [busy, setBusy] = useState(false);
  const presets = [100, 200, 500, 1000];

  const topup = async () => {
    if (amount < 50 || amount > 20000) { toast({ title: 'Amount must be ₹50–₹20,000' }); return; }
    setBusy(true);
    try {
      const { data: session } = await axios.post(`${API}/wallet/topup/init`, { amount }, { headers });
      if (session.demo) {
        toast({ title: 'Payment demo mode', description: 'Cashfree not configured — simulating success.' });
        await new Promise((r) => setTimeout(r, 1200));
        await axios.post(`${API}/wallet/topup/complete/${session.order_number}`, {}, { headers });
        toast({ title: 'Wallet credited 🎉', description: `+₹${amount}` });
        onSaved(); onClose();
      } else {
        const { load } = await import('@cashfreepayments/cashfree-js');
        const cashfree = await load({ mode: 'sandbox' });
        await cashfree.checkout({ paymentSessionId: session.payment_session_id, redirectTarget: '_modal' });
        toast({ title: 'Payment received', description: 'Balance will update in a few seconds.' });
        setTimeout(() => { onSaved(); onClose(); }, 3000);
      }
    } catch (e) {
      toast({ title: 'Topup failed', description: e.response?.data?.detail || 'Try again' });
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-end sm:items-center justify-center p-4" onClick={onClose}>
      <div className="card-soft w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-center mb-5"><h3 className="font-semibold text-lg">Top up wallet</h3><button onClick={onClose} className="p-1 rounded-full hover:bg-black/5"><X size={18} /></button></div>
        <div className="grid grid-cols-4 gap-2 mb-4">
          {presets.map((p) => (
            <button key={p} onClick={() => setAmount(p)} className={`py-2 rounded-lg text-sm font-semibold border ${amount === p ? 'bg-navy text-white border-navy' : 'border-black/10 hover:border-navy/30'}`}>₹{p}</button>
          ))}
        </div>
        <label className="text-xs">Custom amount (₹50–₹20,000)</label>
        <input type="number" value={amount} onChange={(e) => setAmount(Number(e.target.value))} className="mt-1 w-full px-3 py-2.5 rounded-lg border border-black/15" />
        <p className="text-xs text-neutral-500 mt-2">You'll receive {amount} points · 1 point = ₹1 at checkout.</p>
        <button disabled={busy} onClick={topup} className="btn-primary w-full justify-center mt-5 disabled:opacity-60">{busy ? 'Processing…' : `Pay ₹${amount}`}</button>
      </div>
    </div>
  );
}

function FuelModal({ vehicles, onClose, onSaved, headers }) {
  const [f, setF] = useState({ vehicle_id: vehicles[0]?.id || '', fuel_type: 'petrol', amount: '', litres: '', odometer: '', date: new Date().toISOString().slice(0, 10), notes: '' });
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (!f.amount || !f.odometer) { toast({ title: 'Amount and odometer required' }); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/fuel-logs`, {
        vehicle_id: f.vehicle_id,
        fuel_type: f.fuel_type,
        amount: Number(f.amount),
        litres: f.litres ? Number(f.litres) : null,
        odometer: Number(f.odometer),
        date: f.date,
        notes: f.notes,
      }, { headers });
      toast({ title: 'Fill-up added' });
      onSaved(); onClose();
    } catch (e) { toast({ title: 'Failed', description: e.response?.data?.detail || 'Try again' }); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-end sm:items-center justify-center p-4" onClick={onClose}>
      <div className="card-soft w-full max-w-lg p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-center mb-5"><h3 className="font-semibold text-lg">Add fuel fill-up</h3><button onClick={onClose} className="p-1 rounded-full hover:bg-black/5"><X size={18} /></button></div>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2"><label className="text-xs">Vehicle</label>
            <select value={f.vehicle_id} onChange={(e) => setF({ ...f, vehicle_id: e.target.value })} className="mt-1 w-full px-3 py-2.5 rounded-lg border border-black/15">
              {vehicles.map((v) => <option key={v.id} value={v.id}>{v.make} {v.model} ({v.number_plate})</option>)}
            </select>
          </div>
          <div className="col-span-2"><label className="text-xs">Fuel type</label>
            <div className="grid grid-cols-4 gap-2 mt-1">
              {FUEL_TYPES.map((t) => (
                <button key={t.id} type="button" onClick={() => setF({ ...f, fuel_type: t.id })} className={`py-2 rounded-lg text-sm font-semibold border-2 capitalize ${f.fuel_type === t.id ? 'bg-navy text-white border-navy' : 'border-black/10 hover:border-navy/30'}`}>{t.name}</button>
              ))}
            </div>
          </div>
          <Field label="Amount (₹)" value={f.amount} onChange={(v) => setF({ ...f, amount: v })} />
          <Field label="Litres" value={f.litres} onChange={(v) => setF({ ...f, litres: v })} />
          <Field label="Odometer (km)" value={f.odometer} onChange={(v) => setF({ ...f, odometer: v })} />
          <Field label="Date" value={f.date} onChange={(v) => setF({ ...f, date: v })} placeholder="YYYY-MM-DD" />
          <div className="col-span-2"><label className="text-xs">Notes</label><textarea rows={2} value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} className="mt-1 w-full px-3 py-2.5 rounded-lg border border-black/15 resize-none" /></div>
        </div>
        <button disabled={busy} onClick={save} className="btn-primary w-full justify-center mt-5 disabled:opacity-60">{busy ? 'Saving…' : 'Save fill-up'}</button>
      </div>
    </div>
  );
}

function OrderTimeline({ status, tracking_id, carrier }) {
  const steps = [
    { key: 'placed', label: 'Placed' },
    { key: 'packed', label: 'Packed' },
    { key: 'shipped', label: 'Shipped' },
    { key: 'delivered', label: 'Delivered' },
  ];
  if (status === 'cancelled') {
    return <div className="text-xs text-red-600 font-medium">Order cancelled</div>;
  }
  const currentIdx = Math.max(0, steps.findIndex((s) => s.key === status));
  return (
    <div>
      <div className="flex items-center gap-1">
        {steps.map((s, i) => (
          <div key={s.key} className="flex-1 flex items-center gap-1">
            <div className="flex flex-col items-center gap-1 flex-1">
              <div className={`w-3 h-3 rounded-full ${i <= currentIdx ? 'bg-navy' : 'bg-neutral-300'}`}></div>
              <p className={`text-[10px] ${i <= currentIdx ? 'text-navy font-semibold' : 'text-neutral-400'}`}>{s.label}</p>
            </div>
            {i < steps.length - 1 && <div className={`flex-1 h-0.5 mb-4 ${i < currentIdx ? 'bg-navy' : 'bg-neutral-200'}`}></div>}
          </div>
        ))}
      </div>
      {tracking_id && (
        <p className="text-[10px] text-neutral-500 mt-2 text-center">Tracking: {tracking_id}{carrier ? ` · ${carrier}` : ''}</p>
      )}
    </div>
  );
}
